
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import FormModal from "@/components/base/FormModal.vue";
import TrashBtn from "@/components/base/buttons/TrashBtn.vue";
import ImageNotesFile from "@/components/base/ImageNotesFile.vue";
import ImageInput from "@/components/base/form/ImageInput.vue";
import NotesTooltip from "@/components/base/NotesTooltip.vue";
import { IBaseAutocomplete } from "@/types";
import TooltipButton from "@/components/base/buttons/TooltipButton.vue";
import SvgTrash from "@/assets/icons/trash.svg";
import SvgEdit from "@/assets/icons/tooltip-edit.svg";
import BaseAutocomplete from "@/components/base/design/BaseAutocomplete.vue";
import { IAdvertiser, IContractor, IContractorAddress } from "@/api/types/revenue";
import { Advertiser } from "@/services/advertiser/Advertiser";
import { editContractors, getAllContractors } from "@/api/revenue";
import AddressChip from "@/components/revenue/advertiser/AddressChip.vue";
import { ITableHeader, showNotification, showServerError } from "@/utils";
import BaseInput from "@/components-UI/BaseInput.vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import SvgPlus from "@/assets/icons/plus.svg";
import SvgCheck from "@/assets/icons/check.svg";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import { CellsType, prepareItems, RawConfig } from "@/utils/CellsFactory";
import SvgDrag from "@/assets/icons/drag-icon.svg";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";
import SvgPencil from "@/assets/icons/pencil.svg";
import BaseQuestionMarkTooltip from "@/components-UI/BaseQuestionMarkTooltip.vue";
import BaseTextarea from "@/components-UI/BaseTextarea.vue";
import BaseInputNumber from "@/components-UI/BaseInputNumber.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import BaseList from "@/components-UI/list/BaseList.vue";

@Component({
    components: {
        BaseTextarea,
        BaseQuestionMarkTooltip,
        BaseTooltip,
        BaseTable,
        SvgCheck,
        BaseButton,
        BaseEditableCard,
        BaseInput,
        AddressChip,
        BaseAutocomplete,
        SvgTrash,
        SvgEdit,
        TooltipButton,
        NotesTooltip,
        ImageInput,
        ImageNotesFile,
        TrashBtn,
        FormModal,
        SvgPlus,
        SvgDrag,
        SvgPencil,
        BaseInputNumber,
        BaseListItem,
        BaseList,
    },
})

export default class AdvertiserContractors extends Vue {
    @Prop({ required: true }) advertiser!: Advertiser;
    @Prop() disableTitleButton!: boolean;

    contractorsItems: IContractor[] | any = [];
    oldContractorsItems: IContractor[] = [];
    isLoading: boolean = false;
    isShowAddressModal: boolean = false;
    isEditAddress: boolean = false;
    editableContractor = {} as { id: number; name: string; };
    addressForm = [{ text: 'Название', key: 'name' }, { text: 'Адрес', key: 'address' }];
    newAddress = {} as IContractorAddress;
    isShowModal: boolean = false;
    newContractor: string = '';
    isEdit: boolean = false;
    emailRegx = new RegExp('^[^\\s@\u0400-\u04FF]+@[^\\s@\u0400-\u04FF]+\\.[a-zA-Z]{2,}$');
    daysToPayRegx = new RegExp(/^\d{1,3}$/);
    headers: ITableHeader[] = [
        { text: 'По умолч.', value: 'is_default', width: '75px', align: 'center' },
        { text: 'Название', value: 'name', width: 'auto' },
        { text: 'ИНН', value: 'inn', width: 'auto' },
        { text: 'Адреса', value: 'address', width: '300px' },
        { text: 'Почта для счетов', value: 'invoicing_email', width: '220px' },
        { text: 'Дополнительные почты', value: 'invoicing_email_additionals', width: 'auto' },
        { text: 'Срок оплаты, дней', value: 'days_to_pay', width: '150px' },
        { text: '', value: 'actions', align: 'center', width: '30px' },
    ];
    config: RawConfig<any> = {
        [CellsType.Uniq]: {
            address: {},
            is_default: {},
            invoicing_email: {},
            invoicing_email_additionals: {},
            days_to_pay: {},
            actions: {},
        },
    };

    get isMatchInvoicingEmail(): (invoicing_email: string) => boolean {
        return (invoicing_email: string): boolean => {
            return !invoicing_email.match(this.emailRegx);
        };
    }

    /** Строка для textarea / отображения (массив с бэка склеиваем) */
    normalizeInvoicingEmailsField(value: unknown): string {
        if (Array.isArray(value)) {
            return value.filter(Boolean).map(String).join('\n');
        }
        if (value == null) return '';
        return String(value);
    }

    /** true — красная обводка / строка попадает в disableSaveButton (пусто допускается) */
    get isMatchInvoicingEmails(): (value: unknown) => boolean {
        return (value: unknown): boolean => {
            const s = this.normalizeInvoicingEmailsField(value).trim();
            if (!s) return false;
            const parts = s.split(/[,;\n]+/).map(p => p.trim()).filter(Boolean);
            return parts.some(p => !p.match(this.emailRegx));
        };
    }

    parseInvoicingEmailsArray(value: unknown): string[] {
        const s = this.normalizeInvoicingEmailsField(value).trim();
        if (!s) return [];
        return s.split(/[,;\n]+/).map(p => p.trim()).filter(Boolean);
    }

    get disableSaveButton(): IContractor[] {
        return (this.contractorsItems as IContractor[]).filter(el =>
            (!!el.invoicing_email && this.isMatchInvoicingEmail(el.invoicing_email))
            || this.isMatchInvoicingEmails(el.invoicing_email_additionals),
        );
    }

    get getFormattedItems(): any {
        const columns = this.headers.map(el => el.value);
        return prepareItems(this.contractorsItems, this.config, columns);
    }

    get isDisableSaveNewAddress(): boolean {
        const { name, address } = this.newAddress;
        return !name || !address;
    }

    async handleClickEditButton(event: boolean): Promise<void> {
        try {
            this.isEdit = event;

            if (!event) {
                for (const i of this.contractorsItems) {
                    const isValid = this.oldContractorsItems.some(el =>
                        i.invoicing_email === el.invoicing_email
                            && Number(i.days_to_pay) === el.days_to_pay
                            && JSON.stringify(this.parseInvoicingEmailsArray(i.invoicing_email_additionals))
                            === JSON.stringify(this.parseInvoicingEmailsArray(el.invoicing_email_additionals)),
                    );

                    if (!isValid) {
                        await editContractors(i.id!, {
                            invoicing_email: i.invoicing_email!,
                            days_to_pay: Number(i.days_to_pay!),
                            invoicing_email_additionals: this.parseInvoicingEmailsArray(i.invoicing_email_additionals),
                        });
                    }
                }

                this.oldContractorsItems = JSON.parse(JSON.stringify(this.contractorsItems));

                showNotification('Контрагенты успешно обновлены');
            }
        } catch (e) {
            showServerError(e, 'Контрагенты не обновились');
            this.handleClickResetButton(false);
        }
    }

    closeModal(): void {
        this.isShowModal = false;
        this.isLoading = false;
    }

    changeContractor(id: number): void {
        this.newContractor = id.toString();
    }

    async setDefaultContractor(item: IContractor): Promise<void> {
        this.isLoading = true;
        await this.advertiser.setDefaultContractor(item);
        this.isLoading = false;
    }

    async addContractor(): Promise<void> {
        this.isLoading = true;
        await this.advertiser.addContractor(this.newContractor);
        this.closeModal();
    }

    async deleteContractor(contractor_id: string): Promise<void> {
        this.isLoading = true;
        await this.advertiser.deleteContractor(contractor_id);
        this.isLoading = false;
    }

    get getContractorData(): IBaseAutocomplete {
        return {
            apiMethod: getAllContractors,
            errorText: 'Контрагент не найден',
            placeholder: 'Введите контрагента',
            itemValue: 'external_id',
            template: (i: IContractor) => i.name,
        };
    }

    async deleteAddress(contractorId: number, addressId: number): Promise<void> {
        this.isLoading = true;
        await this.advertiser.deleteAddress(contractorId, addressId);
        this.isLoading = false;
    }

    setEditableDataToModal( item: IContractor, addressData: IContractorAddress): void {
        const { name, address, id } = addressData;
        this.newAddress.name = name;
        this.newAddress.address = address;
        this.newAddress.id = id;
        this.newAddress.id = id;
        this.isEditAddress = true;
        this.openAddressModal(item);
    }

    openAddressModal(item: IContractor): void {
        const { id, name } = item;
        this.editableContractor = { id, name };
        this.isShowAddressModal = true;
    }

    hideAddressModal(): void {
        this.isShowAddressModal = false;
        this.isEditAddress = false;
        this.newAddress.address = '';
        this.newAddress.name = '';
        this.newAddress.id = undefined;
    }

    async editOrAddAddress(): Promise<void> {
        if (!this.editableContractor.id) {
            return;
        }
        this.isLoading = true;
        await this.advertiser.addOrEditAdvertiserAddress(this.editableContractor.id, this.newAddress, this.isEditAddress);
        this.hideAddressModal();
        this.isLoading = false;
    }

    handleClickResetButton(event: boolean): void {
        this.isEdit = event;
        this.contractorsItems = JSON.parse(JSON.stringify(this.oldContractorsItems));
    }

    @Watch('advertiser', { immediate: true })
    updateContractors(value: IAdvertiser): void {
        if (value?.contractors) {
            this.contractorsItems = JSON.parse(JSON.stringify(value.contractors));
            this.oldContractorsItems = JSON.parse(JSON.stringify(value.contractors));
        }
    }
}
